import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { serviceMeta, Badge } from "@/lib/ui";
import { Plus, Search, Calendar, MapPin } from "lucide-react";
import NotificationsPanel from "@/components/NotificationsPanel";
import InvitesPanel from "@/components/InvitesPanel";
import SafetyCheckIn from "@/components/SafetyCheckIn";
import BadgeCelebration from "@/components/BadgeCelebration";
import RouteMap from "@/components/RouteMap";
import VisitStatus from "@/components/VisitStatus";
import StatementsList from "@/components/StatementsList";
import { Printer } from "lucide-react";
import GuidedTour from "@/components/GuidedTour";
import SimpleHome from "@/components/SimpleHome";
import { useSearchParams } from "react-router-dom";
import ReadAloud, { visitToSpeech } from "@/components/ReadAloud";
import FamilySuggestions from "@/components/FamilySuggestions";

export default function Dashboard() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);

  const load = () => api.get("/requests/mine").then((r) => setRequests(r.data));
  useEffect(() => { load(); }, []);

  const isHelper = user.role === "helper";
  const [printing, setPrinting] = useState(false);
  const openPdf = async (path, name) => {
    setPrinting(true);
    try {
      const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${localStorage.getItem("kindred_token")}` } });
      if (!res.ok) throw new Error();
      const url = URL.createObjectURL(await res.blob());
      const w = window.open(url, "_blank");
      if (!w) { const a = document.createElement("a"); a.href = url; a.download = name; a.click(); }
    } catch { toast.error("Couldn't prepare your printout. Please try again."); }
    finally { setPrinting(false); }
  };
  const printWeek = () => openPdf(`/requests/mine/week.pdf?tz_offset=${new Date().getTimezoneOffset()}`, "kindred-week.pdf");
  const printCard = () => openPdf("/me/fridge-card.pdf", "kindred-fridge-card.pdf");
  const statusColor = { open: "slate", matched: "teal", in_progress: "gold", completed: "green", cancelled: "red" };
  const [params] = useSearchParams();
  const [simple, setSimple] = useState(() => localStorage.getItem("kindred_simple") !== "0");
  const showFull = () => { localStorage.setItem("kindred_simple", "0"); setSimple(false); };
  const showSimple = () => { localStorage.setItem("kindred_simple", "1"); setSimple(true); };
  if (!isHelper && simple && params.get("view") !== "full") {
    return (<><GuidedTour user={user} /><SimpleHome user={user} requests={requests} onPosted={load} onShowFull={showFull} onPrintCard={printCard} /></>);
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
      <GuidedTour user={user} />
      {!isHelper && (
        <button data-testid="simple-mode-btn" onClick={showSimple} className="mb-4 text-[#2E7D6B] font-semibold underline underline-offset-4 min-h-[44px]">← Back to simple view</button>
      )}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold text-slate-900">Welcome, {user.name.split(" ")[0]}</h1>
          <p className="text-lg text-slate-600 mt-1">
            {isHelper ? "Requests you've offered to help with." : "Your requests for help."}
          </p>
        </div>
        {isHelper ? (
          <Link to="/browse" className="bg-[#2E7D6B] hover:bg-[#256658] text-white font-semibold px-6 min-h-[48px] rounded-xl flex items-center gap-2 transition-colors">
            <Search className="w-5 h-5" /> Find requests
          </Link>
        ) : (
          <div className="flex flex-wrap gap-3">
            <button data-testid="print-week-btn" onClick={printWeek} disabled={printing}
              className="border-2 border-[#E2D9CE] hover:border-[#2E7D6B] disabled:opacity-60 text-slate-800 font-semibold px-5 min-h-[48px] rounded-xl flex items-center gap-2 transition-colors bg-white">
              <Printer className="w-5 h-5" /> {printing ? "Preparing…" : "Print my week"}
            </button>
            <button data-testid="print-card-btn" onClick={printCard} disabled={printing}
              className="border-2 border-[#E2D9CE] hover:border-[#2E7D6B] disabled:opacity-60 text-slate-800 font-semibold px-5 min-h-[48px] rounded-xl flex items-center gap-2 transition-colors bg-white">
              <Printer className="w-5 h-5" /> Fridge card
            </button>
            <Link to="/post" className="bg-[#D96B43] hover:bg-[#C25832] text-white font-semibold px-6 min-h-[48px] rounded-xl flex items-center gap-2 transition-colors">
              <Plus className="w-5 h-5" /> Post a request
            </Link>
          </div>
        )}
      </div>

      {isHelper && <BadgeCelebration />}
      {isHelper && <RouteMap />}
      {isHelper && <InvitesPanel onAccepted={load} />}
      {!isHelper && <FamilySuggestions requests={requests} onChanged={load} />}

      {!isHelper && requests.filter((r) => ["pending", "alerted"].includes(r.safety_status)).map((r) => (
        <div key={r.id} className="mb-6"><SafetyCheckIn request={r} onChanged={load} compact /></div>
      ))}

      {requests.length === 0 ? (
        <div className="bg-white border border-[#E2D9CE] rounded-2xl p-10 text-center">
          <p className="text-xl font-semibold text-slate-800">Nothing here yet.</p>
          <p className="text-slate-600 mt-1">
            {isHelper ? "Browse open requests and offer your help." : "Post your first request and get matched with a caring helper."}
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-5">
          {requests.filter((r) => r.status !== "draft").map((r) => {
            const meta = serviceMeta(r.service_type);
            return (
              <Link to={`/requests/${r.id}`} key={r.id} data-testid={`my-request-${r.id}`}
                className="bg-white border border-[#E2D9CE] rounded-2xl p-6 shadow-sm hover:shadow-md transition-all">
                <div className="flex items-center justify-between mb-3">
                  <span className="w-11 h-11 rounded-xl bg-[#FBEBE4] flex items-center justify-center">
                    <meta.icon className="w-6 h-6 text-[#D96B43]" />
                  </span>
                  <div className="flex items-center gap-2">
                    <ReadAloud text={visitToSpeech(r, isHelper)} testId={`read-request-${r.id}`} />
                    <Badge color={statusColor[r.status]}>{r.status.replace("_", " ")}</Badge>
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-slate-900">{r.title}</h3>
                <div className="mt-3 space-y-1 text-slate-600">
                  <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#2E7D6B]" /> {r.location}</p>
                  <p className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#2E7D6B]" /> {r.scheduled_date}</p>
                  {isHelper && <p className="text-slate-500">Seeker: {r.seeker_name}</p>}
                  {!isHelper && r.helper_name && <p className="text-slate-500">Helper: {r.helper_name}</p>}
                  <VisitStatus request={r} compact />
                  {r.family_note?.text && <p data-testid={`family-note-hint-${r.id}`} className="text-[#A9741A] text-sm font-semibold">Note from {r.family_note.from}: “{r.family_note.text.slice(0, 60)}{r.family_note.text.length > 60 ? "…" : ""}”</p>}
                </div>
              </Link>
            );
          })}
        </div>
      )}
      {!isHelper && <StatementsList />}
      {!isHelper && <NotificationsPanel />}
    </div>
  );
}
